<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Proxy là gì?</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">Proxy là gì? Hướng dẫn sử dụng Proxy Server 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>15/12/2024</span>
          <span class="mx-2">•</span>
          <span>5 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Kiến thức công nghệ</span>
        </div>
        <div class="h-64 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
          <svg class="w-24 h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-8">
        <div class="prose prose-lg max-w-none">
          <h2 class="mb-2 text-3xl font-bold text-gray-900">I. Proxy Server là gì?</h2>
          <p>
            <strong>Proxy trong mạng Internet</strong> là một máy chủ trung gian (intermediary server) hoạt động giữa 
            thiết bị của bạn (như máy tính, điện thoại) và Internet.
          </p>
          <p>
            Khi bạn gửi một yêu cầu (request) để truy cập một trang web, thay vì gửi trực tiếp, 
            <strong>proxy sẽ tiếp nhận yêu cầu đó, xử lý, và chuyển tiếp đến trang web đích</strong>. 
            Sau đó, nó nhận dữ liệu phản hồi từ trang web và trả lại cho thiết bị của bạn.
          </p>
          <div class="bg-blue-50 p-4 rounded-lg my-6">
            <p class="text-blue-800 font-semibold mb-2">💡 Lưu ý quan trọng:</p>
            <p class="text-blue-700">
              Proxy chỉ thay đổi <strong>IP public của nhà mạng</strong>, 
              <strong>IP private của thiết bị không bị ảnh hưởng</strong>.
            </p>
          </div>

          <h2>Địa chỉ IP (Internet Protocol Address)</h2>
          <p>Mỗi thiết bị trong mạng đều có một địa chỉ IP để nhận diện và giao tiếp:</p>
          
          <h3>Modem WiFi có 2 loại địa chỉ IP:</h3>
          <ul>
            <li><strong>IP Public:</strong> Địa chỉ được cung cấp bởi nhà cung cấp dịch vụ Internet (ISP), đại diện cho toàn bộ mạng của bạn trên Internet</li>
            <li><strong>IP Private:</strong> Địa chỉ mà modem gán cho các thiết bị trong mạng nội bộ (LAN)</li>
          </ul>

          <div class="bg-gray-50 p-4 rounded-lg my-6">
            <h4 class="font-semibold mb-2">📋 Ví dụ thực tế:</h4>
            <ul class="space-y-1">
              <li><strong>IP Public:</strong> 203.0.113.45</li>
              <li><strong>IP Private:</strong> 192.168.0.1 (modem), 192.168.0.2 (laptop), 192.168.0.3 (điện thoại)</li>
            </ul>
          </div>

          <h2 class="mb-2 text-3xl font-bold text-gray-900">II. Cách hoạt động của Proxy</h2>
          
          <h3>1. Không thiết lập Proxy:</h3>
          <div class="bg-red-50 p-4 rounded-lg my-4">
            <p><strong>Luồng:</strong> Thiết bị → Modem WiFi → IP Public → Internet</p>
          </div>

          <h3>2. Khi có Proxy:</h3>
          <div class="bg-green-50 p-4 rounded-lg my-4">
            <p><strong>Luồng:</strong> Thiết bị → Proxy → Internet</p>
          </div>

          <p class="mt-4">
            Proxy có thể được cấu hình ở mức thiết bị cá nhân hoặc trực tiếp trên modem/router.
          </p>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">III. Các loại Proxy thường gặp</h2>
          
          <div class="space-y-6">
            <div class="border-l-4 border-blue-500 pl-4">
              <h3>🌐 HTTP Proxy</h3>
              <ul class="ml-4 space-y-1">
                <li>• Hoạt động trên giao thức HTTP/HTTPS</li>
                <li>• Dùng để truy cập web</li>
                <li>• <strong>Ví dụ:</strong> Proxy chặn/quản lý truy cập web trong mạng công ty</li>
              </ul>
            </div>

            <div class="border-l-4 border-green-500 pl-4">
              <h3>🔧 SOCKS Proxy</h3>
              <ul class="ml-4 space-y-1">
                <li>• Làm việc ở cấp độ thấp hơn HTTP</li>
                <li>• Hỗ trợ nhiều giao thức (FTP, P2P...)</li>
                <li>• Linh hoạt hơn nhưng chậm hơn</li>
              </ul>
            </div>

            <div class="border-l-4 border-yellow-500 pl-4">
              <h3>👁️ Transparent Proxy</h3>
              <ul class="ml-4 space-y-1">
                <li>• Proxy ẩn, không thay đổi dữ liệu hoặc IP của người dùng</li>
                <li>• Dùng để giám sát hoặc cache dữ liệu</li>
              </ul>
            </div>

            <div class="border-l-4 border-purple-500 pl-4">
              <h3>🕵️ Anonymous Proxy</h3>
              <ul class="ml-4 space-y-1">
                <li>• Giấu IP thật của người dùng khi truy cập Internet</li>
              </ul>
            </div>

            <div class="border-l-4 border-red-500 pl-4">
              <h3>🔄 Reverse Proxy</h3>
              <ul class="ml-4 space-y-1">
                <li>• Proxy hoạt động ở phía máy chủ</li>
                <li>• Thường dùng để tăng hiệu năng, bảo mật, hoặc quản lý lưu lượng</li>
              </ul>
            </div>
          </div>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">IV. Ứng dụng của Proxy</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-2">🔒 Bảo mật và riêng tư</h3>
              <ul class="text-blue-700 space-y-1">
                <li>• Proxy giúp ẩn địa chỉ IP thật của bạn</li>
                <li>• Tăng khả năng bảo mật khi duyệt web</li>
              </ul>
            </div>

            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">🌍 Truy cập nội dung bị chặn</h3>
              <ul class="text-green-700 space-y-1">
                <li>• Nếu bạn bị giới hạn truy cập bởi địa lý</li>
                <li>• Proxy có thể giúp vượt qua</li>
              </ul>
            </div>

            <div class="bg-yellow-50 p-4 rounded-lg">
              <h3 class="font-semibold text-yellow-800 mb-2">📊 Quản lý lưu lượng</h3>
              <ul class="text-yellow-700 space-y-1">
                <li>• Trong môi trường doanh nghiệp</li>
                <li>• Proxy có thể chặn các trang web không phù hợp</li>
                <li>• Hoặc giới hạn băng thông</li>
              </ul>
            </div>

            <div class="bg-purple-50 p-4 rounded-lg">
              <h3 class="font-semibold text-purple-800 mb-2">⚡ Tăng hiệu năng</h3>
              <ul class="text-purple-700 space-y-1">
                <li>• Proxy có thể cache dữ liệu</li>
                <li>• Tăng tốc độ truy cập</li>
              </ul>
            </div>
          </div>

          <h2 class="mt-4 text-3xl font-bold text-gray-900">V. Hướng dẫn thiết lập Proxy trên Android</h2>
          
          <div class="bg-orange-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold text-orange-800 mb-4">📱 Tình huống thực tế:</h3>
            <p class="text-orange-700 mb-4">
              Bạn sử dụng PC và điện thoại truy cập 1 trang web mà sợ bị chặn nếu trùng địa chỉ IP. 
              Bạn muốn thiết lập proxy trên điện thoại Android để tiếp tục truy cập trang web đó.
            </p>
          </div>

          <h3>Bước 1: Chuẩn bị thông tin Proxy Server</h3>
          <ul>
            <li>• Tìm proxy server miễn phí hoặc trả phí</li>
            <li>• Lưu lại thông tin: <strong>Hostname</strong> và <strong>Port</strong></li>
          </ul>

          <h3>Bước 2: Cấu hình trên Android</h3>
          <ol class="space-y-2">
            <li><strong>1.</strong> Truy cập vào <strong>WiFi</strong> trên điện thoại</li>
            <li><strong>2.</strong> Nhấn giữ (hoặc mũi tên phải) → <strong>Sửa đổi mạng</strong></li>
            <li><strong>3.</strong> Chọn <strong>Nâng cao</strong></li>
            <li><strong>4.</strong> <strong>Proxy setting</strong> → <strong>Thủ công (Manual)</strong></li>
            <li><strong>5.</strong> Nhập <strong>hostname</strong> và <strong>port</strong> của proxy</li>
            <li><strong>6.</strong> Lưu cài đặt</li>
          </ol>

          <h3>Bước 3: Kiểm tra Proxy hoạt động</h3>
          <ul>
            <li>• Truy cập trang web: <strong>https://www.whatismyip.com/</strong> (WhatIsMyIp)</li>
            <li>• Kiểm tra địa chỉ IP hiển thị</li>
            <li>• <strong>Nếu IP hiển thị khác với IP Public của modem</strong> → Proxy đã hoạt động thành công</li>
          </ul>

          <h2>Lưu ý quan trọng khi sử dụng Proxy</h2>
          <div class="bg-red-50 p-4 rounded-lg my-6">
            <ul class="space-y-2 text-red-700">
              <li>• <strong>Chọn proxy server đáng tin cậy</strong> - tránh proxy có thể đánh cắp dữ liệu</li>
              <li>• <strong>Kiểm tra tốc độ kết nối</strong> - proxy có thể làm chậm internet</li>
              <li>• <strong>Đảm bảo proxy hỗ trợ HTTPS</strong> - bảo mật dữ liệu quan trọng</li>
              <li>• <strong>Không sử dụng proxy miễn phí cho dữ liệu nhạy cảm</strong> - ngân hàng, thanh toán</li>
              <li>• <strong>Kiểm tra định kỳ</strong> - proxy có thể ngừng hoạt động</li>
            </ul>
          </div>

          <h2 class="text-3xl font-bold text-gray-900">VI. Kết luận</h2>
          <p>
            Proxy server là công cụ hữu ích để tăng cường bảo mật, tăng tốc độ truy cập và vượt qua 
            các hạn chế địa lý. Tuy nhiên, cần lựa chọn proxy phù hợp và sử dụng một cách thông minh 
            để đạt được hiệu quả tối ưu.
          </p>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const title = 'Proxy là gì? Hướng dẫn sử dụng Proxy server 2024'
const desc = 'Tìm hiểu Proxy là gì, cách hoạt động và ứng dụng trong việc bảo mật, tăng tốc độ truy cập internet. Hướng dẫn chi tiết sử dụng Proxy server'
const url = 'https://webservices.vn/blog/proxy-la-gi'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
